import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { useAuth } from "@/contexts/AuthContext";

// Compute YYYY-MM-DD for today/+1/+2 in the user's local timezone.
function ymd(d) { return d.toISOString().slice(0, 10); }

// Status considered "open" (i.e., needs a reminder).
const OPEN_STATUSES = new Set(["pending", "in_progress", "review", "blocked"]);

export default function TaskReminder() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!user || typeof user !== "object") return;
    try {
      // Pull this user's open tasks. Filter client-side to ones due today or
      // in the next 2 days OR already overdue. Server returns the assignee's
      // bucket when filter=mine; we still verify deadline ranges below.
      const { data } = await api.get("/tasks", { params: { filter: "mine", limit: 200 } });
      const today = new Date();
      const t0 = ymd(today);
      const t2 = ymd(new Date(today.getTime() + 2 * 86_400_000));
      const visible = (Array.isArray(data) ? data : [])
        .filter((t) => OPEN_STATUSES.has((t.status || "").toLowerCase()))
        .filter((t) => t.deadline)
        .filter((t) => t.deadline.slice(0, 10) <= t2) // due in next 2 days or earlier
        .sort((a, b) => (a.deadline || "").localeCompare(b.deadline || ""));
      setItems(visible);
      // Compute the badge count: today + overdue.
      const _due = visible.filter((t) => t.deadline.slice(0, 10) <= t0).length; // eslint-disable-line no-unused-vars
    } catch (err) {
      if (err?.response?.status !== 401) logger.error("task reminder load failed:", err);
    }
  }, [user]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, [load]);

  if (!user || typeof user !== "object" || user?.role === "client") return null;

  const todayStr = ymd(new Date());
  const overdueCount = items.filter((t) => t.deadline.slice(0, 10) < todayStr).length;
  const todayCount = items.filter((t) => t.deadline.slice(0, 10) === todayStr).length;
  const badgeCount = overdueCount + todayCount;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative inline-flex">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Task reminders"
            data-testid="task-reminder-trigger"
            className={badgeCount > 0 ? "text-teal hover:text-teal" : ""}
          >
            <CheckSquare size={18} strokeWidth={1.5} />
          </Button>
          {badgeCount > 0 && (
            <span
              className="absolute top-1 right-1 min-w-4 h-4 px-1 rounded-full bg-[#EF4444] text-white text-[10px] flex items-center justify-center pointer-events-none"
              data-testid="task-reminder-count"
            >
              {badgeCount > 9 ? "9+" : badgeCount}
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 max-h-[480px] overflow-hidden flex flex-col"
        data-testid="task-reminder-popover"
      >
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold">Task reminders</div>
            <div className="text-[11px] text-muted-foreground">
              {overdueCount > 0 && (<span className="text-rose-600 mr-2">{overdueCount} overdue</span>)}
              {todayCount} today · {items.length - todayCount - overdueCount} upcoming
            </div>
          </div>
          <Link to="/requests" onClick={() => setOpen(false)} className="text-[11px] text-teal hover:underline">All →</Link>
        </div>
        <div className="flex-1 overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-8 text-center text-xs text-muted-foreground" data-testid="task-reminder-empty">
              No reminders. You're all caught up.
            </div>
          )}
          {items.slice(0, 30).map((t) => {
            const dl = t.deadline?.slice(0, 10);
            const isOverdue = dl && dl < todayStr;
            const isToday = dl === todayStr;
            return (
              <Link
                key={t.id}
                to={`/requests?open=${t.id}`}
                onClick={() => setOpen(false)}
                className="block px-4 py-3 border-b border-border/60 last:border-b-0 hover:bg-teal/5"
                data-testid={`task-reminder-row-${t.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate" title={t.title}>{t.title}</div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {t.client_name || "—"} · {t.department || "task"}
                    </div>
                  </div>
                  <span
                    className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full whitespace-nowrap ${
                      isOverdue
                        ? "bg-rose-100 text-rose-700"
                        : isToday
                          ? "bg-amber-100 text-amber-700"
                          : "bg-teal/10 text-teal"
                    }`}
                  >
                    {isOverdue ? "Overdue" : isToday ? "Today" : dl?.slice(5)}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
